import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Share2, Music, Square, Headphones } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Skeleton } from "@/components/ui/skeleton";
import { startAmbientPad } from "@/lib/ambientMusic";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function useMeditationMusic() {
  const [playing, setPlaying] = useState(false);
  const stopRef = useRef(null);
  const start = async () => {
    if (stopRef.current) return;
    try {
      const tracks = (await api.get("/devotional/music-tracks")).data;
      const list = Array.isArray(tracks) ? tracks : tracks?.tracks || [];
      if (list.length) {
        const a = new Audio(`${BACKEND}${list[Math.floor(Date.now() / 86400000) % list.length].stream_url}`);
        a.loop = true;
        a.volume = 0.35;
        await a.play();
        stopRef.current = () => { a.pause(); a.src = ""; };
      } else {
        stopRef.current = startAmbientPad();
      }
    } catch {
      stopRef.current = startAmbientPad();
    }
    setPlaying(true);
  };
  const stop = () => {
    stopRef.current?.();
    stopRef.current = null;
    setPlaying(false);
  };
  useEffect(() => () => stopRef.current?.(), []);
  return { playing, start, stop };
}

function Section({ label, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-3xl border border-[var(--border)] bg-[var(--surface)] p-6 md:p-8">
      <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">{label}</p>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function Devotional() {
  const { lang } = useApp();
  const navigate = useNavigate();
  const es = lang === "es";
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const music = useMeditationMusic();

  useEffect(() => {
    setData(null);
    setError(false);
    api.get("/devotional/today", { params: { lang: es ? "es" : "en" } })
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [es]);

  const share = async () => {
    const text = `${data.passage.label}\n\n${data.reflection}\n\n${es ? "Oración" : "Prayer"}: ${data.prayer}\n\n— FaithCycle`;
    try {
      if (navigator.share) {
        await navigator.share({ title: es ? "Devocional de hoy" : "Today's devotional", text, url: window.location.origin });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(es ? "Devocional copiado" : "Devotional copied");
      }
    } catch {}
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="devotional-page">
      <button data-testid="devotional-back-btn" onClick={() => navigate("/home")} className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text)] transition-colors">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {es ? "Inicio" : "Home"}
      </button>
      <div>
        <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">{es ? "Devocional diario" : "Daily devotional"}</p>
        <h1 className="font-serif text-4xl md:text-5xl mt-2 tracking-tight" data-testid="devotional-title">
          {data ? data.passage.label : (es ? "Momento con Dios" : "Time with God")}
        </h1>
        {data?.passage?.theme && <p className="mt-2 text-[var(--text-secondary)] italic" data-testid="devotional-theme">{data.passage.theme}</p>}
      </div>

      {error && (
        <p data-testid="devotional-error" className="text-sm text-[var(--text-secondary)]">
          {es ? "El devocional de hoy aún se está preparando. Intenta de nuevo en unos minutos." : "Today's devotional is still being prepared. Please try again in a few minutes."}
        </p>
      )}
      {!data && !error && (
        <div className="space-y-4" data-testid="devotional-loading">
          <Skeleton className="h-20 w-full" /><Skeleton className="h-48 w-full" /><Skeleton className="h-32 w-full" />
        </div>
      )}

      {data && (
        <>
          <ListenCard data={data} es={es} music={music} onShare={share} />
          <Section label={es ? "Lectura" : "Reading"} testid="devotional-scripture">
            <div className="font-serif text-lg leading-relaxed space-y-2">
              {data.scripture_verses.map((v) => (
                <p key={v.verse}><sup className="text-[var(--accent)] mr-1 text-xs">{v.verse}</sup>{v.text}</p>
              ))}
            </div>
            <p className="mt-4 text-xs uppercase tracking-widest text-[var(--text-secondary)]">{data.passage.label} · {data.passage.translation}</p>
          </Section>
          <Section label={es ? "Reflexión" : "Reflection"} testid="devotional-reflection">
            <p className="leading-relaxed whitespace-pre-line">{data.reflection}</p>
          </Section>
          <Section label={es ? "Oración" : "Prayer"} testid="devotional-prayer">
            <p className="font-serif text-lg italic leading-relaxed whitespace-pre-line">{data.prayer}</p>
          </Section>
        </>
      )}
    </div>
  );
}

function ListenCard({ data, es, music, onShare }) {
  return (
    <div className="rounded-3xl border border-[var(--accent)]/40 bg-[var(--highlight)]/40 p-6 space-y-4" data-testid="devotional-listen-card">
      <div className="flex items-center gap-3">
        <Headphones className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
        <p className="text-sm flex-1">{es ? "Escucha el devocional. Al terminar la oración comenzará música suave para meditar." : "Listen to the devotional. Soft music will begin after the prayer for meditation."}</p>
      </div>
      <audio
        data-testid="devotional-audio"
        controls
        preload="none"
        src={`${BACKEND}${data.audio_url}`}
        onPlay={music.stop}
        onEnded={music.start}
        className="w-full"
      />
      <div className="flex flex-wrap gap-3">
        {music.playing ? (
          <button data-testid="devotional-music-stop-btn" onClick={music.stop} className="rounded-full px-4 py-2 text-sm border border-[var(--border)] bg-[var(--bg)] flex items-center gap-2 hover:bg-[var(--surface)] transition-colors">
            <Square className="w-3.5 h-3.5" strokeWidth={1.5} /> {es ? "Detener música" : "Stop music"}
          </button>
        ) : (
          <button data-testid="devotional-music-start-btn" onClick={music.start} className="rounded-full px-4 py-2 text-sm border border-[var(--border)] bg-[var(--bg)] flex items-center gap-2 hover:bg-[var(--surface)] transition-colors">
            <Music className="w-3.5 h-3.5" strokeWidth={1.5} /> {es ? "Solo música" : "Music only"}
          </button>
        )}
        <button data-testid="devotional-share-btn" onClick={onShare} className="rounded-full px-4 py-2 text-sm bg-[var(--accent)] text-white flex items-center gap-2 hover:opacity-90 transition-opacity">
          <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} /> {es ? "Compartir" : "Share"}
        </button>
      </div>
    </div>
  );
}
